import { useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useImmerReducer } from "use-immer";
import { useFutureCostQueries } from "./useFutureCostQueries";
import { useIngredientQuery } from "../../Ingredients/hooks/UseIngredientQueries";
import { formatDate } from "../../../utils";
import dayjs from "dayjs";

// Reducer function to manage local state
const reducer = (draft, action) => {
  switch (action.type) {
    case "REMOVE_ITEM": {
      const { id } = action.payload;

      draft.formData.data = draft.formData.data.filter(
        (item) => item.id !== id,
      );
      break;
    }
    case "SET_DIALOG":
      draft.dialog[action.payload.name] = action.payload.value;
      break;
    case "ADD_INGREDIENTS": {
      const ingredients = action.payload.map((item) => ({
        ...item,
        future_cost_exc_gst: "",
        future_cost_inc_gst: "",
      }));
      draft.formData.data.push(...ingredients);
      break;
    }
    case "UPDATE_INGREDIENTS": {
      const { rowIndex, updates } = action.payload;

      draft.formData.data[rowIndex] = {
        ...draft.formData.data[rowIndex],
        ...updates,
      };
      return;
    }
    case "UPDATE_DATE":
      draft.formData.date = action.payload;
      if (draft.validationErrors.length > 0) draft.validationErrors = [];
      break;
    case "SET_VALIDATION_ERRORS":
      draft.validationErrors = action.payload;
      break;
    case "RESET_VALIDATION_ERRORS":
      draft.validationErrors = [];
      break;
    default:
      break;
  }
};

// Initial state for the form data
const initialFormData = {
  date: null,
  data: [],
};

export const useManageFutureCost = () => {
  const [_, forceUpdate] = useState(0);
  const [state, dispatch] = useImmerReducer(reducer, {
    formData: initialFormData,
    dialog: {
      addIngredient: null,
    },
    validationErrors: [],
  });

  // Fetch tax group list for GST calculations
  const { taxListQuery } = useIngredientQuery({
    isTaxgrouplist: true,
    isTaglist: false,
  });

  // Mutation for creating future costs
  const {
    createFutureQuery: { mutateAsync: createAsync, isPending },
  } = useFutureCostQueries({});

  // Form validator setup
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate },
      element: (message) => message,
      validators: {
        notPastDate: {
          message: "Date cannot be in the past",
          rule: (val) => {
            if (!val) return true;

            const selected = dayjs(val);
            const today = dayjs().startOf("day");

            if (!selected.isValid()) return false;

            return selected.isAfter(today, "day");
          },
          required: false,
        },
      },
    }),
  );

  // Toggle the add ingredient dialog
  const toggleAddIngredientDialog = (status) => {
    return dispatch({
      type: "SET_DIALOG",
      payload: { name: "addIngredient", value: status },
    });
  };

  // Handle input changes in the form
  const onChange = (event, taxGroupId = null, rowIndex) => {
    const { name, value } = event.target;
    if (name === "date") {
      dispatch({ type: "UPDATE_DATE", payload: value });
    } else {
      const taxList = taxListQuery?.data ?? [];
      const IsIncGst = name.startsWith("future_cost_inc_gst");
      const IsExcGst = name.startsWith("future_cost_exc_gst");

      if (IsIncGst || IsExcGst) {
        const tax = taxList.find((t) => t.id === taxGroupId);
        if (typeof tax?.tax_rate !== "number") return;
        const taxRate = tax.tax_rate / 100;
        const numericValue = Number(value) || 0;

        let updates = {};

        if (IsExcGst) {
          updates.future_cost_exc_gst = value;
          updates.future_cost_inc_gst = (
            numericValue +
            numericValue * taxRate
          ).toFixed(5);
        }

        if (IsIncGst) {
          updates.future_cost_inc_gst = value;
          updates.future_cost_exc_gst = (numericValue / (1 + taxRate)).toFixed(
            5,
          );
        }

        dispatch({
          type: "UPDATE_INGREDIENTS",
          payload: {
            rowIndex,
            updates,
          },
        });
      }
    }
  };

  // Format API request payload
  const formatApiRequest = (request) => {
    return {
      dates: [
        {
          costing_date: formatDate(request.date),
          ingredients: request.data.map(
            ({ id, future_cost_exc_gst, future_cost_inc_gst }) => ({
              ingredient_id: id,
              future_cost_exc_gst: Number(future_cost_exc_gst),
              future_cost_inc_gst: Number(future_cost_inc_gst),
            }),
          ),
        },
      ],
    };
  };

  // Handle form submission
  const onSubmit = async (event) => {
    event.preventDefault();
    if (formValidator.current.allValid()) {
      await createAsync(formatApiRequest(state.formData))
        .then(() => dispatch({ type: "REST_VALIDATION_ERRORS" }))
        .catch((error) => {
          if (error.response?.status === 422) {
            const duplicates = error.response?.data?.data?.duplicates;

            if (Array.isArray(duplicates)) {
              dispatch({
                type: "SET_VALIDATION_ERRORS",
                payload: duplicates.map((d) => d.ingredient_id),
              });
            }
          }
        });
    } else {
      formValidator.current.showMessages();
      forceUpdate(_ + 1);
    }
  };

  return {
    state,
    isSubmitting: isPending,
    taxListQuery,
    formValidator,
    dispatch,
    toggleAddIngredientDialog,
    onChange,
    onSubmit,
  };
};
